import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { useForm, Controller } from "react-hook-form";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SaveIcon from "@mui/icons-material/Save";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import Loader from "../../../../components/loader/Loader";
import { grey, red } from "@mui/material/colors";
import { usePermission } from "../../../../hooks/usePermission";

function HolidayBlock() {
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const permissions = usePermission();

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      date: null,
      description: "",
      active_status: true,
    },
  });
  const hasActionPermission = permissions?.edit || permissions?.delete;
  const loadData = async () => {
    try {
      setLoader(true);
      const resp = await axios.get("/blackListSlots");
      setData(resp.data?.data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load holidays");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    reset();
  };

  const onSubmit = async (values) => {
    if (!values.date) {
      toast.error("Please select a holiday date");
      return;
    }

    const payload = {
      date: dayjs(values.date).format("YYYY-MM-DD"),
      description: values.description,
      active_status: values.active_status ? 1 : 0,
    };

    try {
      setLoader(true);
      const resp = selectedRow?.id
        ? await axios.put(`/blackListSlots/${selectedRow.id}`, payload)
        : await axios.post("/blackListSlots", payload);

      if (
        resp.data?.error === false ||
        resp.status === 200 ||
        resp.status === 201
      ) {
        toast.success("Saved successfully");
        handleClose();
        loadData();
      } else {
        toast.error("Save failed");
      }
    } catch (err) {
      toast.error("Server error");
    } finally {
      setLoader(false);
    }
  };

  const handleEdit = (row) => {
    reset({
      date: dayjs(row.date),
      description: row.description,
      active_status: row.active_status === 1,
    });
    setSelectedRow(row);
    setOpen(true);
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    try {
      setLoader(true);
      const resp = await axios.delete(`/blackListSlots/${deleteId}`);
      if (resp.data?.error === false || resp.status === 200) {
        toast.success("Deleted successfully");
        loadData();
      } else {
        toast.error("Delete failed");
      }
    } catch (err) {
      toast.error("Error deleting");
    } finally {
      setLoader(false);
      setDeleteOpen(false);
      setDeleteId(null);
    }
  };

  const toggleStatus = async (id) => {
    const row = data.find((r) => r.id === id);
    if (!row) return;

    const payload = {
      date: row.date,
      description: row.description,
      active_status: row.active_status === 1 ? 0 : 1,
    };

    try {
      setLoader(true);
      await axios.put(`/blackListSlots/${id}`, payload);
      toast.success("Status updated");
      loadData();
    } catch {
      toast.error("Update failed");
    } finally {
      setLoader(false);
    }
  };

  const columns = [
    {
      name: "id",
      label: "S. No.",
      options: {
        customBodyRenderLite: (i) => i + 1,
      },
    },
    {
      name: "date",
      label: "Date",
      options: {
        customBodyRender: (v) => dayjs(v).format("DD MMM YYYY"),
      },
    },
    {
      name: "description",
      label: "Description",
    },
    {
      name: "active_status",
      label: "Active",
      options: {
        customBodyRenderLite: (i) => {
          const row = data[i];
          return (
            <Switch
              size="small"
              checked={row.active_status === 1}
              onChange={() => toggleStatus(row.id)}
            />
          );
        },
      },
    },
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            options: {
              filter: false,
              customBodyRenderLite: (i) => {
                const row = data[i];
                return (
                  <div className="actionBtns">
                    {permissions?.edit && (
                      <Button
                        size="small"
                        disabled={row.active_status === 1}
                        onClick={() => handleEdit(row)}
                      >
                        <EditIcon />
                      </Button>
                    )}
                    {permissions?.delete && (
                      <Button
                        size="small"
                        disabled={row.active_status === 1}
                        onClick={() => handleDeleteClick(row.id)}
                      >
                        <DeleteOutlineOutlinedIcon
                          fontSize="medium"
                          sx={{
                            color:
                              row.active_status === 1 ? grey[600] : red[500],
                          }}
                        />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <h4>Holiday Block</h4>

      <MUIDataTable
        data={data}
        columns={columns}
        options={{ selectableRows: "none" }}
        title={
          permissions.add ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                reset();
                setSelectedRow(null);
                setOpen(true);
              }}
            >
              Add Holiday
            </Button>
          ) : null
        }
      />

      {/* Add / Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedRow ? "Edit Holiday" : "New Holiday"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Holiday Date"
                    value={field.value}
                    onChange={field.onChange}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Description" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="active_status"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label="Active"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="error" variant="contained">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            startIcon={<SaveIcon />}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Holiday</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this holiday?
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            variant="contained"
            onClick={() => setDeleteOpen(false)}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmDelete}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {loader && <Loader />}
    </>
  );
}

export default HolidayBlock;
